import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import {
  deleteChatApi,
  editChatApi,
  getChatHistoryApi,
  markChatAsReadApi,
  deleteInternalChatApi,
  editInternalChatApi,
  getInternalChatHistoryApi,
  markInternalChatAsReadApi,
  getChatGalleryApi,
} from "../../../services/chat.services";
import { getPractitionerSearchApi } from "../../../services/receptionist.services";
import { getUploadUrlApi } from "../../../services/common.services";
import { message, Avatar, theme, Image, Dropdown } from "antd";
import { useWebSocket } from "react-use-websocket/dist/lib/use-websocket";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  ACCESS_TOKEN,
  FacilityID,
  ORGANISATONID,
} from "../../../constants/defaultKeys";
import { getCookie } from "../../../helpers/localStorage";
import { UserContext } from "../../../context/UserContext";
import { db } from "../../../db";
import { useLiveQuery } from "dexie-react-hooks";

import GalleryPanel from "./GalleryPanel";
import ChatPanel from "./ChatPanel";
import { buildPartnersDropdown } from "./PartnersDropdown"; // optional — see note below
import { LeftOutlined } from "@ant-design/icons";
import "./Chat.css";
import partnerImg from "../../../assets/fileicons/Partners.svg";
import internalImg from "../../../assets/fileicons/internal.svg";

// ─── helpers ─────────────────────────────────────────────────────────────────

const stringToColor = (str) => {
  if (!str) return "#667085";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${hash % 360}, 65%, 55%)`;
};

const getInitial = (name) => name?.trim()?.charAt(0)?.toUpperCase() || "?";

export const convertToFormData = (res, file) => {
  const formData = new FormData();
  for (const key in res?.data?.fields)
    formData.append(key, res.data.fields[key]);
  formData.append("file", file);
  return formData;
};

// ─── tab styles ──────────────────────────────────────────────────────────────

const tabStyle = (active, colorPrimary) => ({
  padding: "8px 24px",
  cursor: "pointer",
  fontWeight: active ? 700 : 500,
  fontSize: 14,
  color: active ? colorPrimary : "#6B7280",
  borderBottom: active ? `2px solid ${colorPrimary}` : "2px solid transparent",
  transition: "all 0.2s",
  userSelect: "none",
  whiteSpace: "nowrap",
});

// ─── component ───────────────────────────────────────────────────────────────

const ChatContainer = ({
  patientData,
  mode = "partner",
  isMobile = false,
}) => {
  const isInternal = mode === "internal";

  const { token: antdToken } = theme.useToken();
  const colorPrimary = antdToken.colorPrimary;

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const patientId = params.get("patient");

  const chats = useLiveQuery(() => db.chats.toArray());
  const { userDetails } = useContext(UserContext);

  const token = getCookie(ACCESS_TOKEN);
  const organization = getCookie(ORGANISATONID);
  const facility = getCookie(FacilityID);

  const sessionId = patientData?.admission?.ip_id;
  const partners = patientData?.admission?.partners || [];
  const hasPartners = partners.length > 0;

  // ─── tabs ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("chat"); // "chat" | "gallery"

  // ─── messages ────────────────────────────────────────────────────────────
  const [partnerMessages, setPartnerMessages] = useState([]);
  const [internalMessages, setInternalMessages] = useState([]);
  const messages = isInternal ? internalMessages : partnerMessages;
  const setMessages = isInternal ? setInternalMessages : setPartnerMessages;

  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [editMsg, setEditMsg] = useState(null);
  const [editingMessage, setEditingMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [partnerHasMore, setPartnerHasMore] = useState(true);
  const [internalHasMore, setInternalHasMore] = useState(true);
  const hasMore = isInternal ? internalHasMore : partnerHasMore;
  const setHasMore = isInternal ? setInternalHasMore : setPartnerHasMore;
  const lastMessageId = messages?.[0]?.id;

  // ─── gallery ─────────────────────────────────────────────────────────────
  const [galleryImages, setGalleryImages] = useState([]);
  const [visibleCount, setVisibleCount] = useState(4);
  const [isGalleryLoading, setIsGalleryLoading] = useState(false);

  // ─── misc ─────────────────────────────────────────────────────────────────
  const [activeUsers, setActiveUsers] = useState([]);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // ─── mention (internal only) ──────────────────────────────────────────────
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [mentionLoading, setMentionLoading] = useState(false);

  // ─── websocket ────────────────────────────────────────────────────────────
  const wsUrl = useMemo(() => {
    const base = process.env.REACT_APP_WS_BASE_URL;
    return isInternal
      ? `${base}/ws/interdepartment-chat/${sessionId}_internal/`
      : `${base}/ws/chat/${sessionId}/`;
  }, [sessionId, isInternal]);

  const { sendJsonMessage, lastJsonMessage } = useWebSocket(wsUrl, {
    queryParams: { token, organization, facility },
  });

  // ─── effects ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (messagesEndRef.current && !isLoading) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (activeTab !== "chat" || !messagesEndRef.current) return;
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToBottom);
    });
    const t1 = window.setTimeout(scrollToBottom, 50);
    const t2 = window.setTimeout(scrollToBottom, 200);
    const t3 = window.setTimeout(scrollToBottom, 450);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [activeTab, mode, isLoading, messages?.length]);

  useEffect(() => {
    if (!lastJsonMessage) return;
    switch (lastJsonMessage.message_type) {
      case "message":
      case "attachment":
        setMessages((prev) => {
          const next = [...prev, lastJsonMessage];
          return next.filter(
            (msg, i, self) => i === self.findIndex((m) => m?.id === msg?.id)
          );
        });
        if (
          lastJsonMessage?.attachment?.type?.startsWith("image/") ||
          lastJsonMessage?.attachment?.type === "application/pdf"
        ) {
          setGalleryImages((prev) => {
            const next = [lastJsonMessage, ...prev];
            return next.filter(
              (msg, i, self) => i === self.findIndex((m) => m?.id === msg?.id)
            );
          });
        }
        break;
      case "active_users":
        setActiveUsers(lastJsonMessage?.users || []);
        break;
      case "typing":
        setIsTyping(lastJsonMessage.isTyping);
        break;
      default:
        break;
    }
  }, [lastJsonMessage]);

  useEffect(() => {
    requestAnimationFrame(() =>
      textareaRef.current?.focus?.({ cursor: "end" })
    );
  }, [editingMessage]);

  useEffect(() => {
    if (sessionId) {
      fetchChatHistory();
      fetchGalleryImages();
    }
  }, [sessionId, mode]);

  useEffect(() => {
    if (patientId) fetchGalleryImages();
  }, [patientId]);

  // ─── fetch ────────────────────────────────────────────────────────────────

  const fetchChatHistory = useCallback(async () => {
    if (!sessionId || isLoading || !hasMore) return;
    const container = messagesEndRef.current;
    const prevScrollHeight = container?.scrollHeight;

    // ✅ Declare chatKey at the top so it's available everywhere
    const chatKey = `${sessionId}_${isInternal ? "internal" : "partner"}`;

    setIsLoading(true);
    try {
      const api = isInternal ? getInternalChatHistoryApi : getChatHistoryApi;
      const response = await api(sessionId, {
        limit: 10,
        last_message_id: lastMessageId,
      });
      const newMessages = response?.data?.data || [];

      if (newMessages.length === 0) {
        setHasMore(false);
        return;
      }

      if (!lastMessageId) {
        setMessages(newMessages.reverse());
      } else {
        setMessages((prev) => [...newMessages.reverse(), ...prev]);
      }

      // ✅ chatKey is now accessible here
      await db.transaction("rw", db.chats, async () => {
        let chat = await db.chats.get(chatKey);
        const merged = [...newMessages, ...(chat?.messages || [])];
        const unique = merged.filter(
          (msg, i, self) => i === self.findIndex((m) => m.id === msg.id)
        );
        await db.chats.put({ sessionId: chatKey, messages: unique });
      });

      requestAnimationFrame(() => {
        if (!container) return;
        if (!lastMessageId) {
          container.scrollTop = container.scrollHeight;
        } else {
          container.scrollTop =
            container.scrollHeight - prevScrollHeight + container.scrollTop;
        }
      });

      if (!lastMessageId) {
        const markRead = isInternal
          ? markInternalChatAsReadApi
          : markChatAsReadApi;
        markRead(sessionId).catch(console.error);
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ?? "Failed to load chat history"
      );
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isLoading, hasMore, isInternal, lastMessageId]); // ✅ added lastMessageId to deps

  const fetchGalleryImages = async () => {
    if (!patientId) return;
    setIsGalleryLoading(true);
    try {
      const galleryMode = isInternal ? "internal" : "partner";
      const response = await getChatGalleryApi(patientId, galleryMode);
      const list = response?.data?.data || [];
      setGalleryImages(
        list.filter((msg, i, self) => i === self.findIndex((m) => m?.id === msg?.id))
      );
    } catch (err) {
      if (!isInternal && err?.response?.status === 404) {
        setGalleryImages([]);
      }
    } finally {
      setIsGalleryLoading(false);
    }
  };

  const handleScroll = useCallback(
    (e) => {
      if (e.target.scrollTop < 100 && !isLoading && hasMore) fetchChatHistory();
    },
    [isLoading, hasMore]
  );

  // ─── send ─────────────────────────────────────────────────────────────────

  const handleSendMessage = async () => {
    if (!isInternal && !hasPartners) {
      message.warning("No partners available. You cannot send messages.");
      return;
    }
    if (
      ((!newMessage.trim() && !pendingFile && !editMsg) ||
        (editMsg && !editingMessage.trim()))
    )
      return;

    if (editMsg) {
      try {
        const editApi = isInternal ? editInternalChatApi : editChatApi;
        await editApi(sessionId, editMsg, { message: editingMessage.trim() });
        fetchChatHistory();
      } catch (error) {
        message.error(
          error?.response?.data?.message || "Failed to edit message"
        );
      }
      setEditMsg(null);
      setEditingMessage("");
      return;
    }

    if (pendingFile) {
      const fileToSend = pendingFile;
      setPendingFile(null);
      await uploadAndSendAttachment(fileToSend);
      return;
    }

    if (isInternal) {
      sendJsonMessage({
        msg_type: "message",
        message: newMessage.trim(),
        ...(taggedUsers.length > 0 && {
          tagged_user_ids: taggedUsers.map((u) => u.user__id),
        }),
      });
      setTaggedUsers([]);
      setShowMentionDropdown(false);
    } else {
      sendJsonMessage({ msg_type: "message", message: newMessage.trim() });
    }
    setNewMessage("");
  };

  // ─── edit / delete ────────────────────────────────────────────────────────

  const handleEditAction = (msgId, msgText) => {
    setEditMsg(msgId);
    setEditingMessage(msgText || "");
  };

  const handleDeleteAction = async (msgId) => {
    try {
      const deleteApi = isInternal ? deleteInternalChatApi : deleteChatApi;
      await deleteApi(sessionId, msgId);
      fetchChatHistory();
    } catch (error) {
      message.error("Failed to delete message");
    }
  };

  // ─── file ─────────────────────────────────────────────────────────────────

  const handleFileClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) {
      message.error("File size exceeds 30MB.");
      return;
    }
    if (!["image/png", "image/jpeg", "application/pdf"].includes(file.type)) {
      message.error("Only PNG, JPG, or PDF files allowed!");
      return;
    }
    setPendingFile(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadAttachmentToS3 = async (file) => {
    const res = await getUploadUrlApi(
      `path_key=${file.name}&admission_type=IP&admission_id=${sessionId}`
    );
    const formData = convertToFormData(res, file);
    await axios.post(res?.data?.url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return { key: res?.data?.fields?.key, type: file.type };
  };

  const sendAttachmentMessage = ({ key, type }) => {
    sendJsonMessage({
      msg_type: "attachment",
      attachment: { path: key, type },
    });
  };

  const uploadAndSendAttachment = async (file) => {
    try {
      const uploaded = await uploadAttachmentToS3(file);
      sendAttachmentMessage(uploaded);
      message.success("File uploaded successfully");
      fetchGalleryImages();
    } catch (err) {
      message.error(err?.response?.data?.message || "Upload failed");
      throw err;
    }
  };

  // ─── mention (internal only) ──────────────────────────────────────────────

  const handleMessageChange = async (e) => {
    const value = e.target.value;
    setNewMessage(value);

    if (!isInternal) return;

    const cursorPos = e.target.selectionStart;
    const mentionMatch = value.slice(0, cursorPos).match(/@(\w*)$/);

    if (mentionMatch) {
      const query = mentionMatch[1];
      setMentionSearch(query);
      setShowMentionDropdown(true);
      if (query.length >= 1) {
        setMentionLoading(true);
        try {
          const res = await getPractitionerSearchApi(
            `query=${query}&facility=${facility}&role=practitioners`
          );
          setMentionResults(res?.data || []);
        } catch (_) {
          setMentionResults([]);
        } finally {
          setMentionLoading(false);
        }
      } else {
        setMentionResults([]);
      }
    } else {
      setShowMentionDropdown(false);
      setMentionSearch("");
      setMentionResults([]);
    }
  };

  const handleMentionSelect = (user) => {
    const mentionName = `${user.first_name}${
      user.last_name ? " " + user.last_name : ""
    }`;
    setNewMessage((prev) => prev.replace(/@(\w*)$/, `${mentionName} `));
    setTaggedUsers((prev) =>
      prev.find((u) => u.user__id === user.user__id) ? prev : [...prev, user]
    );
    setShowMentionDropdown(false);
    setMentionResults([]);
  };

  const handleRemoveTag = (userId) =>
    setTaggedUsers((prev) => {
      const toRemove = prev.find((u) => u.user__id === userId);
      if (toRemove) {
        const name = `${toRemove.first_name}${toRemove.last_name ? " " + toRemove.last_name : ""}`;
        setNewMessage((msg) =>
          (msg || "")
            .replace(new RegExp(`@?${name}\\s*`, "g"), "")
            .replace(/\s{2,}/g, " ")
        );
      }
      return prev.filter((u) => u.user__id !== userId);
    });

  // ─── partners header ──────────────────────────────────────────────────────

  const partnersHeader =
    !isInternal && hasPartners ? (
      <Dropdown
        overlay={buildPartnersDropdown(partners, activeUsers)}
        trigger={["click"]}
        placement="bottomRight"
      >
        <div style={{ cursor: "pointer" }}>
          <Avatar.Group maxCount={5} size={28}>
            {partners.map((item, index) => (
              <Avatar
                key={index}
                size={28}
                style={{
                  backgroundColor: stringToColor(item.name),
                  fontWeight: 900,
                  fontSize: 14,
                }}
              >
                {getInitial(item.name)}
              </Avatar>
            ))}
          </Avatar.Group>
        </div>
      </Dropdown>
    ) : null;

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <div className="chat-container-shell">
      {/* Tab bar */}
      <div
        className={`chat-header-bar ${isMobile ? "chat-header-bar--mobile" : ""}`}
      >
        {isMobile ? (
          <>
            <div className="chat-header-tabs chat-header-tabs--mobile">
              {["chat", "gallery"].map((tab) => (
                <div
                  key={tab}
                  className="chat-header-tab-item"
                  style={tabStyle(activeTab === tab, colorPrimary)}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </div>
              ))}
            </div>
            <div className="chat-header-actions">{partnersHeader}</div>
          </>
        ) : (
          <>
            <div className="chat-header-main">
              <div
                style={{
                  display: "flex",
                  backgroundColor: colorPrimary,
                  borderRadius: "100%",
                  border: "1px solid white",
                  width: "40px",
                  height: "40px",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Image
                  src={mode === "internal" ? internalImg : partnerImg}
                  alt={mode}
                  preview={false}
                />
              </div>

              <span className="chat-header-title">
                {mode === "internal" ? "Internal Chat" : "Partners Chat"}
              </span>

              {["chat", "gallery"].map((tab) => (
                <div
                  key={tab}
                  style={tabStyle(activeTab === tab, colorPrimary)}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </div>
              ))}
            </div>

            <div className="chat-header-actions">{partnersHeader}</div>
          </>
        )}
      </div>
      {/* Tab content */}
      <div className="chat-tab-content">
        {activeTab === "gallery" ? (
          <div className="chat-gallery-scroll">
            <GalleryPanel
              galleryImages={galleryImages}
              isGalleryLoading={isGalleryLoading}
              visibleCount={visibleCount}
              onLoadMore={() => setVisibleCount((v) => v + 4)}
            />
          </div>
        ) : (
          <ChatPanel
            messages={messages}
            isLoading={isLoading}
            isTyping={isTyping}
            sessionId={sessionId}
            colorPrimary={colorPrimary}
            userId={userDetails?.id}
            activeUsers={activeUsers}
            editMsg={editMsg}
            editingMessage={editingMessage}
            setEditingMessage={setEditingMessage}
            onCancelEdit={() => {
              setEditMsg(null);
              setEditingMessage("");
              setNewMessage("");
            }}
            newMessage={newMessage}
            onMessageChange={handleMessageChange}
            onSend={handleSendMessage}
            onFileClick={handleFileClick}
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
            pendingFile={pendingFile}
            onClearPendingFile={() => {
              setPendingFile(null);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
            onRightClick={(e, msg) => {
              /* handled inside ChatMessage */
            }}
            onEditAction={handleEditAction}
            onDeleteAction={handleDeleteAction}
            showMentionDropdown={isInternal ? showMentionDropdown : undefined}
            mentionResults={isInternal ? mentionResults : undefined}
            mentionLoading={isInternal ? mentionLoading : undefined}
            onMentionSelect={isInternal ? handleMentionSelect : undefined}
            taggedUsers={isInternal ? taggedUsers : undefined}
            onRemoveTag={isInternal ? handleRemoveTag : undefined}
            messagesEndRef={messagesEndRef}
            onScroll={handleScroll}
            textareaRef={textareaRef}
            headerRight={null}
            sendDisabled={!isInternal && !hasPartners}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  );
};

export default ChatContainer;
